import { Base } from '../../abstract/base.model';
import { DroneOwnership } from './drone-ownership.model';
import { DroneStatus } from './drone-status.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableString } from '../utils/wire-value-utils';

/**
 * Unified Drone view model for the web portal, built from the hub's drone JSON.
 * Represents both ADN-leased drones (from DroneAssignment/DroneInventory)
 * and organization-owned drones (from OrganizationDrone).
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`), so a form built from a drone can't blank a redacted value. Display fallbacks belong in templates.
 * The hub calls the FAA registration `nNumber`; the web calls it `registrationNumber`.
 */
export class Drone extends Base {
  // Writable fields
  name?: string | null;
  manufacturerId?: string | null;
  modelId?: string | null;
  serialNumber?: string | null;
  registrationNumber?: string | null;
  remoteId?: string | null;
  firmwareVersion?: string | null;
  status?: DroneStatus | null;

  // Read-only display fields, enriched by the hub
  manufacturerName?: string | null;
  modelName?: string | null;
  /** 'adn' = leased, 'organization' = owned. Create/update responses omit it; only org drones can be saved. */
  ownership: DroneOwnership;
  assignedAt?: string | null;
  assignmentId?: string | null;
  totalFlights: number;
  totalAcresSprayed: number;
  totalFlightHours: number;
  lastFlightDate?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.name = nullableString(source['name']);
    this.manufacturerId = nullableString(source['manufacturerId']);
    this.modelId = nullableString(source['modelId']);
    this.serialNumber = nullableString(source['serialNumber']);
    this.registrationNumber = nullableString(source['nNumber']);
    this.remoteId = nullableString(source['remoteId']);
    this.firmwareVersion = nullableString(source['firmwareVersion']);
    this.status = nullableString(source['status']) as DroneStatus | null | undefined;
    this.manufacturerName = nullableString(source['manufacturerName']);
    this.modelName = nullableString(source['modelName']);
    this.ownership = (nullableString(source['ownership']) ?? 'organization') as DroneOwnership;
    this.assignedAt = nullableString(source['assignedAt']);
    this.assignmentId = nullableString(source['assignmentId']);
    this.totalFlights = countOrZero(source['totalFlights']);
    this.totalAcresSprayed = countOrZero(source['totalAcresSprayed']);
    this.totalFlightHours = countOrZero(source['totalFlightHours']);
    this.lastFlightDate = nullableString(source['lastFlightDate']);
  }
}
